import { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ModuleRegistry } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import { AG_GRID_LOCALE_ES } from "@ag-grid-community/locale";
import { ClipboardModule } from "@ag-grid-enterprise/clipboard";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { MultiFilterModule } from "@ag-grid-enterprise/multi-filter";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { MasterDetailModule } from "@ag-grid-enterprise/master-detail";
import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { CsvExportModule } from "@ag-grid-community/csv-export";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";

import {
  BreadCrumbs,
  CardHeaderWidget,
  LoadingWidget,
  SaveAction,
  SelectMultipleWidget2,
} from "../../../components";
import {
  dashAdd,
  alphabeticalSort,
  alterArrayAddLabels,
} from "../../../helpers";
import { FormPrompt } from "../../../helpers/FormPrompt";

import { BsContainer, BsRow, BsCol } from "../../../layouts/components";
import viewsTexts from "../../../common/viewsTexts.json";
import { AxiosContext } from "../../../context/AxiosContext";
import { Context } from "../../../context/Context";
import { useForm } from "react-hook-form";
// import { formatters, validators } from '../formatter_validator';
import "../styles.css";
import { FiltersContext } from "../../../context";
import PhEdit from "../PhEdit";

const useStyles = makeStyles((theme) => ({
  tableContainerStyle: {
    [theme.breakpoints.up("sm")]: {
      paddingRight: 34,
    },
  },
  selectStyle: {
    marginTop: 0,
    marginBottom: 0,
  },
  divider: {
    height: "28px",
    margin: "4px",
  },
  iconButton: {
    padding: "8px",
  },
  actionColumn: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
}));

ModuleRegistry.registerModules([
  FiltersToolPanelModule,
  ClientSideRowModelModule,
  RangeSelectionModule,
  MenuModule,
  MultiFilterModule,
  MasterDetailModule,
  ColumnsToolPanelModule,
  CsvExportModule,
  ExcelExportModule,
  SetFilterModule,
  ClipboardModule,
]);

const endPointFilterName = "v1/libraryExternal/panel-admin";

// Etiqueta de los flags showPlanningHarvest / showTechnicalReport.
// Sin el campo (null/undefined) la línea sigue la regla automática.
const flagLabel = (value) => {
  if (value === true) return "Sí";
  if (value === false) return "No";
  return "Automático";
};

const flagColumn = (headerName, field) => ({
  headerName,
  field,
  valueGetter: (params) => flagLabel(params.data?.[field]),
  editable: false,
  suppressFillHandle: true,
  filter: "agSetColumnFilter",
  filterParams: {
    suppressMiniFilter: true,
  },
  suppressMovable: true,
  enableRowGroup: true,
  width: 150,
});

const titleModule = "PhList";

const PhList = () => {
  const theme = useTheme();
  const axiosContext = useContext(AxiosContext);
  const { userId, userHuerto, userName, userOrchard } = useContext(Context);
  const { state: filterState, addFilter } = useContext(FiltersContext);
  const [filterOptionsState, setFilterOptionsState] = useState(null);
  const gridApiRef = useRef(null);
  const { orchard_filter } = filterState;
  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    params.api.sizeColumnsToFit();
  };
  const localeText = AG_GRID_LOCALE_ES;
  // TABLA
  const [confirmPromptState, setConfirmPromptState] = useState(false);
  const [tableContainerStyleState, setTableContainerStyleState] = useState({
    // height: '80vh',
    width: "100%",
    minHeight: 800,
  });
  const [tableStyleState, setTableStyleState] = useState({
    height: 700,
  });
  const [fullScreenState, setFullScreenState] = useState(false);
  const [visibleColumnsState, setVisibleColumnsState] = useState([]);
  // const [titleCheckState, setTitleCheckState] = useState(
  //   "Estimación Diaria Confirmada"
  // );
  const [copyButtonState, setCopyButtonState] = useState(false);
  const [rowData, setRowData] = useState([]);
  const paginationPageSizeSelector = [20, 50, 100, 500, 1000];
  const [quickFilterText, setQuickFilterText] = useState(""); // estado para el filtro de busquedageneral
  const [loadingSubmitFilterState, setLoadingSubmitFilterState] =
    useState(false);
  const [loadingFilterDataState, setLoadingFilterDataState] = useState(false);
  const [successSubmitState, setSuccessSubmitState] = useState(false);
  const [openDialogState, setOpenDialogState] = useState(false);
  const [openWarningState, setOpenWarningState] = useState(false);
  const [openErrorState, setOpenErrorState] = useState(false);
  const [errorDataState, setErrorDataState] = useState(null);
  const [openModalEditState, setOpenModalEditState] = useState(false);
  const [selectedRowState, setSelectedRowState] = useState(null);

  const [technicBossOptions, setTechnicBossOptions] = useState([]);
  const [productorIdOptions, setProductorIdOptions] = useState([]);
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [csgOptions, setCsgOptions] = useState([]);
  const [orchardOptions, setOrchardOptions] = useState([]);
  const [filterDataState, setFilterDataState] = useState([]);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  // crear un ref de alert
  const alertRef = useRef(false);

  const classes = useStyles();

  // Renderizador personalizado para la columna de acciones
  const ActionCellRenderer = (params) => {
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip title="Editar">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedRowState(params.data);
              setOpenModalEditState(true);
            }}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const handleCloseModalEdit = () => {
    setOpenModalEditState(false);
    setSelectedRowState(null);
  };

  const handleEditSuccess = () => {
    // Recargar la tabla con los datos actuales
    handleSubmit(onSubmit)();
  };

  // Helper functions para navegar por la estructura anidada
  const getTechnicBossObj = (data, technicBossValue) => {
    return data.find((technicBoss) => technicBoss.value === technicBossValue);
  };

  const getProductorIdObj = (technicBossObj, productorIdValue) => {
    return technicBossObj?.productorID?.find(
      (productor) => productor.value === productorIdValue
    );
  };

  const getSpecieObj = (productorIdObj, specieValue) => {
    return productorIdObj?.species?.find(
      (specie) => specie.value === specieValue
    );
  };

  const getCsgObj = (specieObj, csgValue) => {
    return specieObj?.csg?.find((csg) => csg.value === csgValue);
  };

  const clearSelects = (setValue, fieldsToClear) => {
    fieldsToClear.forEach((field) => {
      setValue(field, null);
    });
  };

  const handleClickOpenDialog = () => {
    setOpenDialogState(true);
  };

  const watchTechnicBoss = watch("technicBoss", "");
  const watchProductorId = watch("productorId", "");
  const watchSpecie = watch("specie", "");
  const watchCsg = watch("csg", "");

  const onSubmit = async (values) => {
    if (alertRef.current) {
      handleClickOpenDialog();
      return;
    }
    setLoadingSubmitFilterState(true);
    const dataQuery = {};

    if (values?.technicBoss?.value) {
      dataQuery.technicBossValue = values.technicBoss.value;
    }
    if (values?.productorId?.value) {
      dataQuery.productorID = values.productorId.value;
    }
    if (values?.specie?.value) {
      dataQuery.specieValue = values.specie.value;
    }
    if (values?.csg?.value) {
      dataQuery.csg = values.csg.value;
    }
    if (values?.orchard?.value) {
      dataQuery.orchardValue = values.orchard.value;
    }

    try {
      const response = await axiosContext.authAxios.post(
        endPointFilterName,
        dataQuery
      );
      const { data, status } = response;
      if (status === 200) {
        setVisibleColumnsState(finalColumns);
        setLoadingSubmitFilterState(false);
        const rowDataFullTransform = processDataRowDetailFull(data);
        setRowData(rowDataFullTransform);
      }
    } catch (error) {
    } finally {
      setLoadingSubmitFilterState(false);
    }
  };

  const handleQuickFilterChange = (event) => {
    setQuickFilterText(event.target.value);
  };

  const clearFocusSelection = () => {
    if (gridApiRef.current && gridApiRef.current) {
      gridApiRef.current.clearFocusedCell();
    }

    setConfirmPromptState(true);
  };

  // procesa las filas con su detalle histórico
  const processDataRowDetailFull = (data) => {
    // const { table } = data;
    const rows = [];

    data.forEach((item) => {
      const row = {
        id: item.id,
        client: item.client,
        clientValue: item.clientValue,
        season: item?.season,
        agroBusiness: item.agroBusiness,
        agroBusinessValue: item.agroBusinessValue,
        orchard: item.orchard,
        orchardValue: item.orchardValue,
        specie: item.specie,
        specieValue: item.specieValue,
        variety: item.variety,
        varietyValue: item.varietyValue,
        productLine: item.productLine,
        productLineValue: item.productLineValue,
        productorID: item.productorID,
        csg: item.csg,
        technicBoss: item.technicBoss,
        technicBossValue: item.technicBossValue,
        group: item.group,
        groupValue: item.groupValue,
        // null = la línea no tiene el campo ("Automático")
        showPlanningHarvest: item.showPlanningHarvest ?? null,
        showTechnicalReport: item.showTechnicalReport ?? null,
      };
      rows.push(row);
    });

    return rows;
  };

  const handleCloseWarningAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenWarningState(false);
    setErrorDataState(null);
  };

  const handleCloseErrorAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenErrorState(false);
    setErrorDataState(null);
  };

  const finalColumns = [
    {
      headerName: "Cliente",
      field: "client",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Temporada",
      field: "season",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Agrícola",
      field: "agroBusiness",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Huerto",
      field: "orchard",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Especie",
      field: "specie",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Variedad",
      field: "variety",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Linea de producto",
      field: "productLine",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Productor ID",
      field: "productorID",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "CSG",
      field: "csg",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Jefe Técnico",
      field: "technicBoss",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Grupo",
      field: "group",
      editable: false,
      suppressFillHandle: true,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    flagColumn("Planificación", "showPlanningHarvest"),
    flagColumn("Informe técnico", "showTechnicalReport"),
    {
      headerName: "Acción",
      field: "action",
      cellRenderer: ActionCellRenderer,
      editable: false,
      suppressFillHandle: true,
      filter: false,
      suppressMovable: true,
      enableRowGroup: false,
      width: 100,
    },
  ];

  // función para setear el fullScreen
  const setFullScreen = (fullScreenState) => {
    if (fullScreenState) {
      // document.body.style.overflow = 'hidden';

      setTableContainerStyleState({
        width: "100%",
        zIndex: 1250,
        top: 0,
        left: 0,
        position: "fixed",
        overflow: "auto",
        height: "100vh",
      });

      setTableStyleState({
        height: "calc(100vh - 90px)",
      });
    } else {
      document.body.style.overflow = "";
      setTableContainerStyleState({
        // height: '80vh',
        width: "100%",
        minHeight: 800,
      });

      setTableStyleState({
        height: 700,
      });
    }
    setFullScreenState((prevState) => !prevState);
  };

  // genera el sidebar Custom
  const sideBar = {
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressPivotMode: true, // desactivar el modo pivot
          suppressRowGroups: true, // desactivar la agrupación de filas
          suppressValues: true, // desactivar los valores
          suppressColumnFilter: true, // desactivar el filtro de busqueda en las columnas
          contractColumnSelection: true, // contraer la selección de columnas al incio
          suppressColumnSelectAll: true, // desactivar el check de selección de todas las columnas
        },
        minWidth: 225,
        maxWidth: 225,
        width: 225,
      },
      // {
      // 	id: 'custom',
      // 	labelDefault: 'Personalizadas',
      // 	labelKey: 'customStats',
      // 	iconKey: 'columns',
      // 	toolPanel: customPanel,
      // 	toolPanelParams: {},
      // },

      // {
      //     id: 'filters',
      //     labelDefault: 'Filters',
      //     labelKey: 'filters',
      //     iconKey: 'filter',
      //     toolPanel: 'agFiltersToolPanel',
      //     minWidth: 180,
      //     maxWidth: 400,
      //     width: 250
      // }
    ],
    position: "right",
    //   defaultToolPanel: 'customPanel',
  };

  // funcion para edición de celdas
  const onCellValueChanged = (params) => {
    const { data, colDef, newValue, oldValue } = params;

    if (newValue === null || newValue === "") {
      data[`edited_${colDef.field}`] = false; // Marca la celda como editada
      params.node.setDataValue(colDef.field, oldValue); // Restaura el valor anterior
    } else {
      if (newValue !== oldValue && newValue !== "") {
        data[`edited_${colDef.field}`] = true; // Marca la celda como editada
        params.api.refreshCells({
          rowNodes: [params.node],
          columns: [colDef.field],
          force: true,
        });
      }

      alertRef.current = true;
      setCopyButtonState(true);

      // Actualizar el estado de rowData
      const updatedRowData = [...rowData];

      const rowIndex = updatedRowData.findIndex((row) => row.id === data.id);
      updatedRowData[rowIndex] = data;

      setRowData(updatedRowData);
    }
  };

  const handleClosePrompt = () => {
    // setSuccessSubmitState(false);
    setConfirmPromptState(false);
  };

  const onResetSearch = () => {
    setQuickFilterText("");
  };

  const onSaveData = async () => {
    // setLoadingSubmitState(true);

    clearFocusSelection();
  };

  const closeHandler = () => {
    setSuccessSubmitState(false);
  };

  const onCloseSnack = () => {
    // setSuccessSubmitState(false);
    handleClosePrompt();
  };

  const handleCloseDialog = () => {
    setOpenDialogState(false);
  };

  const handleCloseAndConfirmDialog = () => {
    setCopyButtonState(false);
    alertRef.current = false;
    handleSubmit(onSubmit)();

    setOpenDialogState(false);
  };

  // FILTROS DE LOS SELECT
  const getFilterAsync = useCallback(async () => {
    setLoadingFilterDataState(true);
    try {
      let response = await axiosContext.authAxios.get(endPointFilterName);

      const { data, status } = response;

      if (status === 200) {
        // Guardar la estructura completa
        setFilterDataState(data);

        // Extraer solo los technicBoss para el primer select
        const technicBosses = data.map((technicBoss) => ({
          label: technicBoss.label,
          value: technicBoss.value,
        }));
        setTechnicBossOptions(alphabeticalSort(technicBosses, "label"));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingFilterDataState(false);
    }
  }, [axiosContext]);

  useEffect(() => {
    getFilterAsync();
  }, [getFilterAsync]);

  // useEffect para technicBoss -> productorId
  useEffect(() => {
    if (watchTechnicBoss && watchTechnicBoss.value && filterDataState.length) {
      clearSelects(setValue, ["productorId", "specie", "csg", "orchard"]);
      const technicBossObj = getTechnicBossObj(
        filterDataState,
        watchTechnicBoss.value
      );
      if (technicBossObj && technicBossObj.productorID) {
        const productorIdData = technicBossObj.productorID.map((productor) => ({
          label: productor.label,
          value: productor.value,
        }));
        setProductorIdOptions(alphabeticalSort(productorIdData, "label"));
      } else {
        setProductorIdOptions([]);
      }
    } else {
      clearSelects(setValue, ["productorId", "specie", "csg", "orchard"]);
      setProductorIdOptions([]);
    }
    // Limpiar opciones dependientes
    setSpeciesOptions([]);
    setCsgOptions([]);
    setOrchardOptions([]);
  }, [watchTechnicBoss, filterDataState, setValue]);

  // useEffect para productorId -> specie
  useEffect(() => {
    if (
      watchTechnicBoss &&
      watchTechnicBoss.value &&
      watchProductorId &&
      watchProductorId.value &&
      filterDataState.length
    ) {
      clearSelects(setValue, ["specie", "csg", "orchard"]);
      const technicBossObj = getTechnicBossObj(
        filterDataState,
        watchTechnicBoss.value
      );
      if (technicBossObj) {
        const productorIdObj = getProductorIdObj(
          technicBossObj,
          watchProductorId.value
        );
        if (productorIdObj && productorIdObj.species) {
          const speciesData = productorIdObj.species.map((specie) => ({
            label: specie.label,
            value: specie.value,
          }));
          setSpeciesOptions(alphabeticalSort(speciesData, "label"));
        } else {
          setSpeciesOptions([]);
        }
      }
    } else {
      clearSelects(setValue, ["specie", "csg", "orchard"]);
      setSpeciesOptions([]);
    }
    // Limpiar opciones dependientes
    setCsgOptions([]);
    setOrchardOptions([]);
  }, [watchTechnicBoss, watchProductorId, filterDataState, setValue]);

  // useEffect para specie -> csg
  useEffect(() => {
    if (
      watchTechnicBoss &&
      watchTechnicBoss.value &&
      watchProductorId &&
      watchProductorId.value &&
      watchSpecie &&
      watchSpecie.value &&
      filterDataState.length
    ) {
      clearSelects(setValue, ["csg", "orchard"]);
      const technicBossObj = getTechnicBossObj(
        filterDataState,
        watchTechnicBoss.value
      );
      if (technicBossObj) {
        const productorIdObj = getProductorIdObj(
          technicBossObj,
          watchProductorId.value
        );
        if (productorIdObj) {
          const specieObj = getSpecieObj(productorIdObj, watchSpecie.value);
          if (specieObj && specieObj.csg) {
            const csgData = specieObj.csg.map((csg) => ({
              label: csg.label,
              value: csg.value,
            }));
            setCsgOptions(alphabeticalSort(csgData, "label"));
          } else {
            setCsgOptions([]);
          }
        }
      }
    } else {
      clearSelects(setValue, ["csg", "orchard"]);
      setCsgOptions([]);
    }
    // Limpiar opciones dependientes
    setOrchardOptions([]);
  }, [
    watchTechnicBoss,
    watchProductorId,
    watchSpecie,
    filterDataState,
    setValue,
  ]);

  // useEffect para csg -> orchard
  useEffect(() => {
    if (
      watchTechnicBoss &&
      watchTechnicBoss.value &&
      watchProductorId &&
      watchProductorId.value &&
      watchSpecie &&
      watchSpecie.value &&
      watchCsg &&
      watchCsg.value &&
      filterDataState.length
    ) {
      clearSelects(setValue, ["orchard"]);
      const technicBossObj = getTechnicBossObj(
        filterDataState,
        watchTechnicBoss.value
      );
      if (technicBossObj) {
        const productorIdObj = getProductorIdObj(
          technicBossObj,
          watchProductorId.value
        );
        if (productorIdObj) {
          const specieObj = getSpecieObj(productorIdObj, watchSpecie.value);
          if (specieObj) {
            const csgObj = getCsgObj(specieObj, watchCsg.value);
            if (csgObj && csgObj.orchard) {
              const orchardData = csgObj.orchard.map((orchard) => ({
                label: orchard.label,
                value: orchard.value,
              }));
              setOrchardOptions(alphabeticalSort(orchardData, "label"));
            } else {
              setOrchardOptions([]);
            }
          }
        }
      }
    } else {
      clearSelects(setValue, ["orchard"]);
      setOrchardOptions([]);
    }
  }, [
    watchTechnicBoss,
    watchProductorId,
    watchSpecie,
    watchCsg,
    filterDataState,
    setValue,
  ]);

  // Restaurar filtros guardados para orchard (mantener solo este)
  useEffect(() => {
    if (orchardOptions.length && orchard_filter) {
      const found = orchardOptions.find(
        (element) => element.value === orchard_filter
      );
      if (found) {
        setValue("orchard", found);
      }
    }
  }, [orchardOptions, orchard_filter, setValue]);

  return (
    <div className="form-style">
      <BsContainer fluid class="px-0 px-md-3">
        <BsRow class="no-gutters">
          <BsCol class="col-md-12">
            <Card elevation={0}>
              <CardHeaderWidget
                title={viewsTexts.titles.bdcollection.identification.title}
                subheader={<BreadCrumbs subTitle="" />}
              />
              <CardContent
                sx={{
                  paddingX: 0,
                  paddingTop: 0,
                  paddingBottom: "0 !important",
                }}
              >
                {/* PARA AVISAR DATOS NO GUARDADOS */}
                <>
                  {/* para avisar número mayores de 99999 */}
                  {errorDataState && (
                    <>
                      <Snackbar
                        open={openWarningState}
                        autoHideDuration={6000}
                        onClose={handleCloseWarningAlert}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "center",
                        }}
                        style={{ top: "18vh" }} // Aquí es donde la magia sucede
                      >
                        <Alert
                          onClose={handleCloseWarningAlert}
                          severity="warning"
                          variant="filled"
                          sx={{
                            width: "100%",
                            fontSize: 18,
                          }}
                        >
                          {`${errorDataState.field}:  ${errorDataState.message}`}
                        </Alert>
                      </Snackbar>
                      <Snackbar
                        open={openErrorState}
                        autoHideDuration={6000}
                        onClose={handleCloseErrorAlert}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "center",
                        }}
                        style={{ top: "25vh" }} // Aquí es donde la magia sucede
                      >
                        <Alert
                          onClose={handleCloseErrorAlert}
                          severity="error"
                          variant="filled"
                          sx={{
                            width: "100%",
                            fontSize: 18,
                          }}
                        >
                          VALOR INGRESADO MAYOR
                        </Alert>
                      </Snackbar>
                    </>
                  )}
                  <FormPrompt hasUnsavedChanges={copyButtonState} />
                  <PhEdit
                    open={openModalEditState}
                    onClose={handleCloseModalEdit}
                    selectedRow={selectedRowState}
                    onSuccess={handleEditSuccess}
                  />
                  <Dialog
                    open={openDialogState}
                    keepMounted
                    onClose={handleCloseDialog}
                    aria-describedby="alert-dialog-slide-description"
                    className="max-w-md mx-auto"
                  >
                    <DialogContent>
                      <DialogContentText
                        id="alert-dialog-slide-description"
                        className="font-medium"
                      >
                        Tienes cambios sin guardar. Si continúas, se perderán
                        los datos editados.
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions className="py-4 mr-2">
                      <Button onClick={handleCloseDialog}>Cancelar</Button>
                      <Button
                        onClick={handleCloseAndConfirmDialog}
                        variant="contained"
                        color="error"
                      >
                        Continuar sin guardar
                      </Button>
                    </DialogActions>
                  </Dialog>
                  <BsRow class="no-gutters">
                    <BsCol class="col-md-12">
                      <div style={tableContainerStyleState}>
                        <div
                          // className='d-flex justify-content-between pb-3 pt-2'
                          className="d-flex flex-column flex-md-row justify-content-md-between justify-content-center pb-3 pt-2 gap-3"
                          style={{
                            backgroundColor: "white",
                          }}
                        >
                          <Box
                            sx={{
                              justifyContent: "flex-start",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <form
                              className="form-inline"
                              autoComplete="on"
                              onSubmit={handleSubmit(onSubmit)}
                            >
                              <div className="input-group ml-md-2">
                                <SelectMultipleWidget2
                                  errors={errors.technicBoss}
                                  loading={loadingFilterDataState}
                                  control={control}
                                  menuPortalTarget={document.body}
                                  labeltext=""
                                  name="technicBoss"
                                  display="vertical"
                                  variant="outlined"
                                  isClearable={true}
                                  isSearchable
                                  isMulti={false}
                                  req={false}
                                  defaultValue=""
                                  options={technicBossOptions}
                                  placeholder="Jefe Técnico"
                                />
                              </div>
                              <div className="input-group ml-md-2">
                                <SelectMultipleWidget2
                                  errors={errors.productorId}
                                  loading={loadingFilterDataState}
                                  control={control}
                                  menuPortalTarget={document.body}
                                  labeltext=""
                                  name="productorId"
                                  display="vertical"
                                  variant="outlined"
                                  isClearable={true}
                                  isSearchable
                                  isMulti={false}
                                  req={false}
                                  defaultValue=""
                                  options={productorIdOptions}
                                  placeholder="Productor"
                                />
                              </div>
                              <div className="input-group ml-md-2">
                                <SelectMultipleWidget2
                                  errors={errors.specie}
                                  loading={loadingFilterDataState}
                                  control={control}
                                  menuPortalTarget={document.body}
                                  labeltext=""
                                  name="specie"
                                  display="vertical"
                                  variant="outlined"
                                  isClearable={true}
                                  isSearchable
                                  isMulti={false}
                                  req={false}
                                  defaultValue=""
                                  options={speciesOptions}
                                  placeholder="Especie"
                                />
                              </div>
                              <div className="input-group ml-md-2">
                                <SelectMultipleWidget2
                                  errors={errors.csg}
                                  loading={loadingFilterDataState}
                                  control={control}
                                  menuPortalTarget={document.body}
                                  labeltext=""
                                  name="csg"
                                  display="vertical"
                                  variant="outlined"
                                  isClearable={true}
                                  isSearchable
                                  isMulti={false}
                                  req={false}
                                  defaultValue=""
                                  options={csgOptions}
                                  placeholder="CSG"
                                />
                              </div>
                              <div className="input-group ml-md-2">
                                <SelectMultipleWidget2
                                  errors={errors.orchard}
                                  loading={loadingFilterDataState}
                                  control={control}
                                  menuPortalTarget={document.body}
                                  labeltext=""
                                  name="orchard"
                                  display="vertical"
                                  variant="outlined"
                                  isClearable={true}
                                  isSearchable
                                  isMulti={false}
                                  req={false}
                                  defaultValue=""
                                  options={orchardOptions}
                                  placeholder="Huerto"
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  alignSelf: "flex-start",
                                  paddingTop: 5,
                                }}
                              >
                                <SaveAction
                                  title="Filtrar"
                                  color="secondary"
                                  size="medium"
                                  className="roundedButton ml-md-2"
                                  variant="contained"
                                  disabled={loadingSubmitFilterState}
                                  messageType="add"
                                  mutationLoading={loadingSubmitFilterState}
                                  success={false}
                                />
                              </div>
                            </form>
                          </Box>
                          <Box
                            sx={{
                              // marginRight: 2,
                              marginBottom: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 2,
                            }}
                          >
                            <TextField
                              variant="outlined"
                              placeholder="Buscar..."
                              label="Buscar"
                              size="small"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon />
                                  </InputAdornment>
                                ),
                                endAdornment: quickFilterText ? (
                                  <IconButton
                                    color="primary"
                                    aria-label="clear"
                                    component="span"
                                    onClick={() => onResetSearch()}
                                  >
                                    <ClearIcon
                                      fontSize="small"
                                      style={{
                                        color: theme.palette.grey[400],
                                      }}
                                    />
                                  </IconButton>
                                ) : null,
                              }}
                              value={quickFilterText}
                              onChange={handleQuickFilterChange}
                              sx={{
                                width: 200,
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                            }}
                          >
                            {/* <IconButton
                              color="primary"
                              aria-label="export"
                              onClick={handleMenuOpen}
                              disabled={
                                loadingSubmitFilterState || rowData.length === 0
                              }
                            >
                              <DownloadIcon />
                            </IconButton> */}

                            <IconButton
                              color="primary"
                              aria-label="fullscreen-toggle"
                              onClick={() => setFullScreen(!fullScreenState)}
                              disabled={loadingSubmitFilterState}
                            >
                              {fullScreenState ? (
                                <FullscreenExitIcon />
                              ) : (
                                <FullscreenIcon />
                              )}
                            </IconButton>
                          </Box>
                        </div>
                        {loadingSubmitFilterState && <LoadingWidget />}
                        {/* 
                        {confirmPromptState ? (
                          <Dialog
                            open={confirmPromptState}
                            onClose={handleClosePrompt}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                          >
                            <DialogTitle
                              id="alert-dialog-title"
                              sx={{
                                lineHeight: 0.5,
                              }}
                            >
                              Edición {titleModule}
                            </DialogTitle>
                            <DialogContent>
                              <DialogContentText
                                id="alert-dialog-description"
                                className={classes.dialogTextStyle}
                              >
                                ¿Confirma que desea actualizar los datos?
                              </DialogContentText>
                            </DialogContent>
                            <DialogActions
                              sx={{
                                justifyContent: "space-between",
                              }}
                            >
                              <Button
                                onClick={handleClosePrompt}
                                disabled={loadingSubmitState}
                                sx={{
                                  color: theme.palette.grey[500],
                                }}
                              >
                                Cancelar
                              </Button>
                              {/* <ButtonAction
																labelButton="GUARDAR"
																color="secondary"
																// size='large'
																actionClose={
																	onCloseSnack
																}
																variant="contained"
																fullWidth={true}
																actionOnClick={
																	onAcceptPrompt
																}
																disabled={false}
																messageType="edit"
																mutationLoading={
																	loadingSubmitState
																}
																success={false}
															/> 
                            </DialogActions>
                          </Dialog>
                        ) : null} */}

                        {!loadingSubmitFilterState &&
                          (rowData && rowData?.length ? (
                            <div
                              className="ag-theme-quartz"
                              style={tableStyleState}
                            >
                              <AgGridReact
                                rowData={rowData}
                                columnDefs={visibleColumnsState}
                                onCellValueChanged={onCellValueChanged}
                                pagination={true}
                                paginationPageSize={50}
                                suppressMovableColumns={true}
                                suppressDragLeaveHidesColumns={true}
                                paginationPageSizeSelector={
                                  paginationPageSizeSelector
                                }
                                localeText={localeText}
                                defaultColDef={{
                                  resizable: true,
                                  minWidth: 200, // Ancho mínimo
                                  floatingFilter: true,
                                  filter: false,
                                }}
                                //   masterDetail={true}
                                //   detailCellRendererParams={
                                //     detailCellRendererParams
                                //   }
                                groupDisplayType={"multipleColumns"}
                                rowGroupPanelShow={"always"}
                                enableRangeSelection={true}
                                enableFillHandle={true}
                                quickFilterText={quickFilterText}
                                onGridReady={onGridReady}
                                sideBar={sideBar}
                                embedFullWidthRows={true}
                                // isRowMaster={isRowMaster}
                                // detailRowAutoHeight={true}
                                getRowId={(params) => params.data.id}
                                // grandTotalRow={'bottom'}
                              />
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "70vh",
                                width: "100%",
                              }}
                            >
                              <Typography variant="h5" color="textSecondary">
                                No hay datos
                              </Typography>
                            </div>
                          ))}
                      </div>
                    </BsCol>
                  </BsRow>
                </>
              </CardContent>
            </Card>
          </BsCol>
        </BsRow>
      </BsContainer>
    </div>
  );
};

export default PhList;
